import { ZuiBaseElement } from '@zywave/zui-base';
import { html } from 'lit';
import { state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { style } from './zui-tab-css.js';

/**
 * @element zui-tab
 *
 * @slot unnamed - Pass tab text into this slot
 *
 * @cssprop [--zui-tabs-color=var(--zui-blue)] - Highlighted color of the selected tab, impacts text and border color
 *
 * @event tabsizechange - Fired when a tab's size changes; i.e. a text change, slot modification, etc.
 * @event tabselected - Fired when a tab is selected via user input and also programmatically selected
 */

export class ZuiTabElement extends ZuiBaseElement {
  static get styles() {
    return [super.styles, style];
  }

  @query('.tab')
  private _tabEl: HTMLDivElement;

  /**
   * This private field allows `zui-tabs` to internally "select" a `zui-tab`. DO NOT USE EXTERNALLY
   */
  @state()
  private _selected = false;

  firstUpdated() {
    new ResizeObserver(() => {
      this.dispatchEvent(new CustomEvent('tabsizechange', { bubbles: true, composed: true, detail: { tab: this } }));
    }).observe(this);
  }

  render() {
    return html`
      <div
        tabindex="0"
        aria-role="tab"
        aria-selected="${this._selected}"
        @click=${this.#handleUserEvent}
        @keyup=${this.#handleUserEvent}
        class="tab ${classMap({ selected: this._selected })}"
      >
        <slot></slot>
      </div>
    `;
  }

  #handleUserEvent(event: Event) {
    if (event instanceof KeyboardEvent && !(event.key === 'Enter' || event.key === ' ')) {
      return;
    }
    this.dispatchEvent(
      new CustomEvent('tabselected', {
        bubbles: true,
        composed: true,
        detail: {
          tab: this,
        },
      })
    );
    this.#animateRipple();
  }

  #animateRipple() {
    this._selected && this._tabEl.classList.add('ripple');
  }
}

window.customElements.define('zui-tab', ZuiTabElement);

declare global {
  interface HTMLElementTagNameMap {
    'zui-tab': ZuiTabElement;
  }
}
